/**
 * Header Component
 * Uses shadcn/ui components for consistent styling
 */

import { useAuth, useLogout } from '@/hooks/useAuth';
import { useAppStore } from '@/stores/app';
import { Bell, Sun, Moon, LogOut, User as UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export function Header() {
	const { user } = useAuth();
	const logout = useLogout();
	const { theme, setTheme } = useAppStore();
	const navigate = useNavigate();

	const toggleTheme = () => {
		const newTheme = theme === 'light' ? 'dark' : 'light';
		setTheme(newTheme);
		document.documentElement.classList.toggle('dark', newTheme === 'dark');
	};

	const handleLogout = () => {
		logout.mutate();
	};

	const userInitial = user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U';

	return (
		<TooltipProvider>
			<header className="flex h-16 items-center justify-between border-b bg-card px-6">
				{/* Search */}

				{/* Actions */}
				<div className="flex items-center gap-2">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button variant="ghost" size="icon" onClick={toggleTheme}>
								{theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
							</Button>
						</TooltipTrigger>
						<TooltipContent>Toggle theme</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Button variant="ghost" size="icon">
								<Bell className="h-5 w-5" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>Notifications</TooltipContent>
					</Tooltip>

					{/* User Menu */}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" className="relative h-9 w-9 rounded-full">
								<Avatar className="h-9 w-9">
									<AvatarFallback className="bg-primary text-primary-foreground">{userInitial}</AvatarFallback>
								</Avatar>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							<DropdownMenuLabel className="font-normal">
								<div className="flex flex-col space-y-1">
									<p className="text-sm font-medium leading-none">
										{user?.first_name} {user?.last_name}
									</p>
									<p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
								</div>
							</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => navigate('/admin/profile')}>
								<UserIcon className="mr-2 h-4 w-4" />
								<span>Profile</span>
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onClick={handleLogout}
								disabled={logout.isPending}
								className="text-destructive focus:text-destructive"
							>
								<LogOut className="mr-2 h-4 w-4" />
								<span>Log out</span>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</header>
		</TooltipProvider>
	);
}
